///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Component, Input, OnInit } from '@angular/core';
import { GtthLayerDTO, PlannerFeatureCollectionDTO } from '../../../../../generated/shared-types.model';
import { FormGroup } from '@angular/forms';
import { GtthLogicalTopology } from '../../../../../project-view-panel/logical-topology/entities/logical-topology';
import { ProjectDialogComponent } from '../../project-dialog.component';
import { convertFeetToMeter, convertMeterToFeet } from '../../../../../shared/utils';
import { GtthReactiveTextbox } from '../../../../../shared/reactive-forms/input-text';
import { GtthReactiveNumber } from '../../../../../shared/reactive-forms/input-number';
import { GtthInputControlService } from '../../../../../shared/reactive-forms/input-control.service';
import { GtthLayerDtoModel } from '../../../../../services/project-socket/dto/gtth-layer-dto.model';
import { GtthConfigProperties } from '../../../../../services/logical-topology/config-properties.model';
import { SuiToggleControlComponent, SuiTypeToggle } from '@siklu/sui-lib/infrastructure';
import { ProjectUnits } from '../../../../project-enums';
import { GtthReactiveFormInputComponent } from '../../../../../shared/reactive-forms/reactive-form-input.component';
import { SuiTranslatePipe } from '@siklu/sui-lib';
import { GtthReactiveFormsModule } from '../../../../../shared/reactive-forms/reactie-forms.module';

@Component({
    selector: 'winde-project-dialog-layer-general',
    templateUrl: 'project-dialog-layer-general-panel.component.html',
    styles: [
        `
            .blockedByPoleControl {
                display: flex;
                flex-direction: column;
            }

            .form-label {
                display: inline-block;
                margin-bottom: 0.5rem;
            }
        `
    ],
    imports: [GtthReactiveFormsModule, SuiToggleControlComponent, SuiTranslatePipe, GtthReactiveFormInputComponent]
})
export class ProjectDialogLayerGeneralPanelComponent implements OnInit {
    @Input() viewInReadOnly: boolean;
    @Input() logicalConfig: PlannerFeatureCollectionDTO;
    public editForm: FormGroup;
    public activeLayerIndex: number;
    public layerNameInput: GtthReactiveTextbox;
    public requiredRateInput: GtthReactiveNumber;
    public defaultHeightInput: GtthReactiveNumber;
    public isBlockedByPoleToggle: SuiTypeToggle;
    public blockedByPoleAngle: GtthReactiveNumber;

    constructor(private readonly inputControlService: GtthInputControlService) {}

    @Input() set layerIndex(index: number) {
        this.activeLayerIndex = index;
        this.refreshForm();
    }

    ngOnInit(): void {
        this.initForm();
    }

    initForm(): void {
        const layerNameIsUnique = (name: string): boolean => {
            const findDuplicate = this.logicalConfig.crs.properties.layers.find((layer: GtthLayerDTO, index: number) => {
                return index !== this.activeLayerIndex && layer.layerName.trim() === name.trim();
            });

            return findDuplicate === undefined;
        };

        this.layerNameInput = GtthLayerDtoModel.getLayerNameField(layerNameIsUnique);
        this.layerNameInput.disabled = this.viewInReadOnly;

        this.requiredRateInput = GtthConfigProperties.getRequiredRateField();

        this.defaultHeightInput = GtthConfigProperties.getDefaultHeightField();
        this.defaultHeightInput.disabled = this.viewInReadOnly;
        this.isBlockedByPoleToggle = GtthConfigProperties.getIsBlockedByPoleField(
            this.logicalConfig.crs.properties.isBlockedByPole[this.activeLayerIndex] || false,
            this.viewInReadOnly
        );
        this.blockedByPoleAngle = GtthConfigProperties.getBlockedByPoleDegreesField();
        this.blockedByPoleAngle.disabled = this.viewInReadOnly;

        this.editForm = this.inputControlService.toFormGroup([
            this.layerNameInput,
            this.requiredRateInput,
            this.defaultHeightInput,
            this.blockedByPoleAngle
        ]);

        this.refreshForm();
    }

    refreshForm(): void {
        if (!this.editForm) {
            return;
        }

        this.editForm.get(this.layerNameInput.key).setValue(this.logicalConfig.crs.properties.layers[this.activeLayerIndex].layerName);
        this.editForm
            .get(this.requiredRateInput.key)
            .setValue(
                this.logicalConfig.crs.properties.tuRequiredRatePerLayer[this.activeLayerIndex] ||
                    this.logicalConfig.crs.properties.tuRequiredRatePerLayer[GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX]
            );
        this.isBlockedByPoleToggle.toggleValue(this.logicalConfig.crs.properties.isBlockedByPole[this.activeLayerIndex] || false);
        this.editForm
            .get(this.blockedByPoleAngle.key)
            .setValue(this.logicalConfig.crs.properties.autodesignP2mpPerLayer[this.activeLayerIndex].pole_block);

        let heightValue =
            this.logicalConfig.crs.properties.defaultHeightPerLayer[this.activeLayerIndex] || ProjectDialogComponent.DEFAULT_HEIGHT;

        if (this.logicalConfig.crs.properties.productUnits === ProjectUnits.FEET) {
            heightValue = convertMeterToFeet(heightValue);
        }

        this.editForm.get(this.defaultHeightInput.key).setValue(heightValue);
    }

    validateAndApply(): boolean {
        if (this.editForm.invalid) {
            this.inputControlService.formSubmitEventBroadcast(this.editForm);
            return false;
        }

        this.logicalConfig.crs.properties.layers[this.activeLayerIndex].layerName = this.editForm.get(this.layerNameInput.key).value;
        this.logicalConfig.crs.properties.tuRequiredRatePerLayer[this.activeLayerIndex] = this.editForm.get(
            this.requiredRateInput.key
        ).value;
        this.logicalConfig.crs.properties.isBlockedByPole[this.activeLayerIndex] = this.isBlockedByPoleToggle.getValue();
        this.logicalConfig.crs.properties.autodesignP2mpPerLayer[this.activeLayerIndex].pole_block = this.editForm.get(
            this.blockedByPoleAngle.key
        ).value;

        let heightValue = this.editForm.get(this.defaultHeightInput.key).value;
        if (this.logicalConfig.crs.properties.productUnits === ProjectUnits.FEET) {
            heightValue = convertFeetToMeter(heightValue);
        }
        this.logicalConfig.crs.properties.defaultHeightPerLayer[this.activeLayerIndex] = heightValue;

        return true;
    }
}
